<script lang="ts" setup>
import type InputTextBaseExpose from '@/objects/InputTextBaseExpose';
import { randomstring } from '@/utilities/utils';
import { computed, ref, watch } from 'vue';
import type { IEmitFocus } from '../Interfaces/IEmitFocus';
import type { IEmitBlur } from '../Interfaces/IEmitBlur';
import type { IInputTextBaseProps } from './PropInterfaces/IInputTextBaseProps';
import { IconComp, MarkText, TooltipHelp } from '..';
import { isCommonKey } from '@/utilities/keyboardutils';

defineOptions({ inheritAttrs: false });

// The ref of the input element
const inputref = ref<HTMLInputElement | null>(null);

// Properties we can pass to the component
const props = withDefaults(defineProps<IInputTextBaseProps>(), {
    placeholder: '',
    selectOnFocus: false,

    readonly: false,
    labelPosition: 'left',
});

// The emits available
const emits = defineEmits<
    {
        /**
         * Raised when the value changes
         */
        (e: 'update:modelValue', value: string): void;

        /**
         * Raised when the pattern does not match
         */
        (e: 'patternMismatch', event: Event): void;

        /**
         * When the value has changed
         */
        (e: 'change', newvalue: string | null, oldvalue: string | null): void;

        /**
         * Whenever the input has changed
         */
        (e: 'changed', newvalue: string | null, oldvalue: string | null): void;
    } & IEmitFocus &
        IEmitBlur
>();

const modelValue = defineModel<string>({
    required: true,
});

let lazyValue = ref(modelValue.value ?? '');

const oldValue = ref(modelValue.value ?? '');

// Update the lazyValue when the modelValue changes
watch(
    () => modelValue.value,
    (newVal, ov) => {
        oldValue.value = ov ?? '';
        if (newVal !== lazyValue.value) {
            lazyValue.value = newVal;
        }
    },
);

/**
 * We need an internal representation of the value to allow for lazy updates
 * This also handles all the other modifiers
 */
const internalValue = computed({
    get: () => {
        return lazyValue.value;
    },
    set: (value) => {
        // TODO: Move the validation into the base
        // if (!Validate(value)) return '';

        if (props.modelModifiers?.trim) {
            value = value.trim();
        }

        if (props.modelModifiers?.uppercase) {
            value = value.toUpperCase();
        }

        if (props.modelModifiers?.lowercase) {
            value = value.toLowerCase();
        }

        if (props.modelModifiers?.capitalize) {
            value = value.charAt(0).toUpperCase() + value.slice(1);
        }

        lazyValue.value = value;

        if (!props.modelModifiers?.lazy) {
            modelValue.value = value;
        }
    },
});

/**
 * Focus on the element
 * @param start move the carret to the start
 */
const focus = (start?: boolean) => {
    inputref.value?.focus();
    if (start) {
        inputref.value?.setSelectionRange(0, 0);
    }
    if (props.selectOnFocus) {
        inputref.value?.select();
    }
};

const isFocused = ref(false);

/**
 * Raised when the input has focus
 */
const focused = (ev: Event) => {
    isFocused.value = true;
    inputref.value?.checkValidity();

    emits('focus', ev);
};

/**
 * Raised when the input has lost focus
 */
const blurred = (ev: Event) => {
    isFocused.value = false;

    emits('changed', modelValue.value, oldValue.value);

    oldValue.value = modelValue.value ?? '';

    // This runs to trigger the `invalid` event if the input is invalid. https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/checkValidity
    inputref.value?.checkValidity();

    // If we are lazy, we update the model value on blur
    if (props.modelModifiers?.lazy) {
        modelValue.value = lazyValue.value;
    }

    emits('blur', ev);
};

const cancelEnter = (ev: KeyboardEvent) => {
    // We need to stop propagation of all common keys
    if (ev.key === 'Enter') {
        ev.preventDefault();
    } else if (ev.key !== 'Tab' && ev.key != 'Escape' && ev.key != 'ArrowUp' && ev.key != 'ArrowDown' && isCommonKey(ev)) {
        ev.stopPropagation();
    }
};

/**
 * Unique id for the input
 * @example 'i-1234'
 */
const guid = randomstring('i-', '-');

/**
 * Since the javascript HTML element doesnt trigger a change event if the value is changed via script and not keyboard input we need to handle that manually.
 * We watch the modelValue and emit a change when the value changes.
 */
watch(
    () => modelValue.value,
    (nv, ov) => {
        if (nv != ov) {
            emits('change', nv, ov);
        }
    },
    { once: true },
);

defineExpose<InputTextBaseExpose>({
    isFocused: () => isFocused.value,
    focus,

    /**
     * Handling keyboard input events
     * @param ev
     */
    KeyboardEvent: (ev: KeyboardEvent) => {
        if (inputref.value) {
            const event = new KeyboardEvent(ev.type, ev);
            inputref.value.dispatchEvent(event);
        }
    },
});
</script>

<template>
    <span class="flex-fill d-flex align-items-center input-text">
        <MarkText
            v-if="props.label"
            :text="$tm(props.label)"
            :markPart="props.markTextPart"
            tag-type="label"
            class="form-label textlabel"
            :class="[props.labelPosition == 'left' ? 'text-end me-4' : '']"
        />
        <div
            class="flex-fill d-flex"
            :class="{
                border: props.labelPosition != 'top' || props.border,
                'border-success': props.border == 'success',
                'border-danger': props.border == 'danger',
                'border-warning': props.border == 'warning',
                'border-info': props.border == 'info',
            }"
        >
            <div class="form-control-wrapper flex-fill position-relative">
                <input
                    :readonly="props.readonly"
                    :disabled="props.disabled"
                    spellcheck="false"
                    class="form-control"
                    v-model="internalValue"
                    ref="inputref"
                    :id="props.id || guid"
                    :class="[$attrs.class, { 'bg-primary bg-opacity-10': props.disabled }]"
                    :placeholder="$tm(props.placeholder)"
                    :pattern="props.pattern"
                    @blur="blurred"
                    @invalid="emits('patternMismatch', $event)"
                    @focus="focused"
                    @keydown="cancelEnter"
                    v-bind="$attrs"
                    :autofocus="props.autofocus"
                    :autocomplete="props.autocomplete"
                    :maxlength="props.maxlength"
                    :data-locked="props.locked === true ? 'true' : undefined"
                    :tabindex="props.tabindex"
                />
                <IconComp
                    v-if="props.locked === true"
                    icon="fa-lock"
                    class="position-absolute bottom-0 end-0 overlay-1 fa-xs opacity-50"
                    :style="{ 'margin-bottom': '1px', 'margin-right': '1px' }"
                />
            </div>
            <span
                class="input-group-text"
                v-if="icon"
            >
                <IconComp :icon="icon" />
            </span>
            <span
                class="input-group-text"
                v-else-if="posttext"
            >
                {{ $tm(props.posttext!) }}
            </span>
            <TooltipHelp
                v-if="props.tooltip"
                :text="props.tooltip"
                class="ms-auto"
            />
        </div>
    </span>
</template>
